/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useSetRecoilState } from "recoil";
import { CategoriesAPI } from "../../../api/Categories";
import { CategoriesAtom } from "../../../atoms/CategoriesAtom";
import { CategoriesMoreActionsPopOverLoadingAtom } from "../../../atoms/MoreActionsPopoverAtom";
import { TagsAtom } from "../../../atoms/TagsAtom";
import useClickOutside from "../../../hook/useClickOutside";
import { customTooltip, Flex, notSelect } from "../../../styles/general";
import { ICategory } from "../../../types/CategoryTypes";
import IconBerserkerMail from "../../Icon";
import CreateOrUpdateCategory from "../../Modal/container/Tags/CreateOrUpdateCategory";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";

const Container = styled.div`
  position: relative;
  margin-left: 5px;
  color: #2f2f2f;
  ${notSelect}
`;

interface PopoverProps {
  bottomPopover: boolean;
}

const Popover = styled.div<PopoverProps>`
  height: fit-content;
  width: 240px;

  padding: 10px;

  position: absolute;
  z-index: 20;

  background: white;
  box-shadow: 0px 3px 6px #00000026;

  top: ${(props) => (props.bottomPopover ? "24px" : "auto")};
  right: -4px;

  overflow: auto;

  border-radius: 5px;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;

  display: flex;
  flex-direction: column;

  cursor: default;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 15px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;

    path {
      fill: white;
    }
  }
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Title = styled.div`
  color: #888888;
  font-size: 14px;
  padding: 10px 15px;
`;

const Text = styled.span`
  margin-left: 8px;
`;

interface Props {
  category: ICategory;
}

export default function MoreActionsPopOver(props: Props): JSX.Element {
  const { category } = props;
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [bottomPopover, setBottomPopover] = useState(true);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const setCurrentCategories = useSetRecoilState(CategoriesAtom);
  const setTags = useSetRecoilState(TagsAtom);
  const containerRef = useRef<HTMLDivElement>(null);
  const modal = useModal();
  const setIsLoading = useSetRecoilState<boolean>(
    CategoriesMoreActionsPopOverLoadingAtom
  );

  useEffect(() => {
    if (!containerRef.current) return;
    let bottom = containerRef.current.getBoundingClientRect().bottom;

    if (!bottomPopover)
      bottom =
        bottom + containerRef.current.getBoundingClientRect().height + 20;

    setBottomPopover(bottom >= 0 && bottom <= window.innerHeight);
  }, [containerRef, openDropView]);

  async function deleteCategory() {
    try {
      setIsLoading(true);
      await CategoriesAPI.remove(category._id!);

      setCurrentCategories((currentCategories) =>
        currentCategories.filter((c) => category._id !== c._id)
      );
      setTags((tags) => tags.filter((t) => t.categoryId !== category._id));
    } catch (error) {
      toast("There was an error, try again later", { type: "error" });
    } finally {
      setIsLoading(false);
      modal.popModal();
    }
  }

  return (
    <Container ref={wrapperRef}>
      <Flex
        style={{ height: "30px", cursor: "pointer" }}
        data-tip
        data-for="moreActions"
        onClick={() => setOpenDropView(!openDropView)}
      >
        <ReactTooltip
          id="moreActions"
          place="bottom"
          backgroundColor="#333333"
          css={customTooltip}
          effect="solid"
          globalEventOff="click"
        >
          <TooltipText>More Actions</TooltipText>
        </ReactTooltip>

        <div
          css={css`
            cursor: pointer;
          `}
          onClick={() => setOpenDropView(!openDropView)}
        >
          <IconBerserkerMail name="threedots" />
        </div>
      </Flex>

      {openDropView && (
        <Popover ref={containerRef} bottomPopover={bottomPopover}>
          <Title>CATEGORY ACTIONS</Title>

          <Row
            onClick={() =>
              modal.pushModal(
                <CreateOrUpdateCategory
                  isEdit
                  category={category}
                  onCancel={() => modal.popModal()}
                />,
                "Update category"
              )
            }
          >
            <IconBerserkerMail
              name="pen"
              color="#BBBBBB"
              height={16}
              width={14}
            />
            <Text>Edit category</Text>
          </Row>

          <Row
            onClick={() => {
              modal.pushModal(
                <Warning
                  icon={"./icons/delete.svg"}
                  title="Delete Category"
                  description={`Are you sure you want to delete your category “${category.name}”? All linked tags to this category will be deleted too`}
                  buttonsName={["No", "Yes"]}
                  IsLoadingAtom={CategoriesMoreActionsPopOverLoadingAtom}
                  onOkClicked={() => {
                    deleteCategory();
                  }}
                  onCancelClicked={() => modal.popModal()}
                />
              );
            }}
          >
            <IconBerserkerMail name="delete" color="#BBBBBB" />
            <Text>Delete category</Text>
          </Row>
        </Popover>
      )}
    </Container>
  );
}
